import { useCallback, useRef, useState } from "react";

export function useDragToScroll<T extends HTMLElement>() {
  const scrollContainerRef = useRef<T>(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const dragOccurredRef = useRef(false);

  // Detect if device supports touch
  const isTouchDevice =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!scrollContainerRef.current || isTouchDevice) return;

      // Allow text selection - don't initiate drag if clicking on text content
      const target = e.target as HTMLElement;
      const isTextNode = target.nodeType === Node.TEXT_NODE;
      const hasTextContent =
        target.textContent && target.textContent.trim().length > 0;
      const isInteractive = target.closest(
        "button, a, input, select, textarea",
      );

      // Skip drag initialization if clicking on text or interactive elements
      if (isTextNode || hasTextContent || isInteractive) {
        const selection = window.getSelection();
        if (selection && selection.toString().length === 0) {
          // Only proceed with drag if no text is currently selected
          setIsMouseDown(true);
          setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
          setScrollLeft(scrollContainerRef.current.scrollLeft);
          scrollContainerRef.current.style.willChange = "scroll-position";
        }
        return;
      }

      setIsMouseDown(true);
      setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
      setScrollLeft(scrollContainerRef.current.scrollLeft);
      scrollContainerRef.current.style.willChange = "scroll-position";
    },
    [isTouchDevice],
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      dragOccurredRef.current = true;
    }
    setIsMouseDown(false);
    setIsDragging(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.willChange = "auto";
    }
  }, [isDragging]);

  const checkAndResetDrag = useCallback(() => {
    if (dragOccurredRef.current) {
      dragOccurredRef.current = false;
      return true;
    }
    return false;
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isMouseDown || !scrollContainerRef.current) return;

      const x = e.pageX - scrollContainerRef.current.offsetLeft;
      const walk = x - startX;

      // Only start dragging after moving more than 5px
      if (!isDragging && Math.abs(walk) > 5) {
        // Check if user is selecting text
        const selection = window.getSelection();
        if (selection && selection.toString().length > 0) {
          // User is selecting text, don't start dragging
          setIsMouseDown(false);
          return;
        }
        setIsDragging(true);
      }

      if (isDragging) {
        e.preventDefault();
        // Use requestAnimationFrame for smooth GPU-accelerated scrolling
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = scrollLeft - walk;
          }
        });
      }
    },
    [isMouseDown, startX, scrollLeft, isDragging],
  );

  const dragHandlers = isTouchDevice
    ? {}
    : {
        onMouseDown: handleMouseDown,
        onMouseUp: handleMouseUp,
        onMouseMove: handleMouseMove,
        onMouseLeave: handleMouseUp,
      };

  return {
    ref: scrollContainerRef,
    isDragging,
    dragHandlers,
    checkAndResetDrag,
  };
}
